import { useEffect, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { chapterApi } from '@/api/chapterApi'
import { friendlyMessage } from '@/api/http'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import type { Chapter, ConflictRow } from '@/types/nf'
import {
  buildContextWindow,
  locateEntity,
  locateEntityApproximately,
  type ConflictContextWindow,
} from '@/utils/conflictContext'

interface ConflictContextDialogProps {
  open: boolean
  row: ConflictRow | null
  chapters: Chapter[]
  onClose: () => void
}

interface ContextPane {
  chapter: Chapter | null
  query: ReturnType<typeof useQuery<Chapter>>
  title: string
}

const FALLBACK_RUNE_LIMIT = 800

function getErrorMessage(error: unknown, fallback: string) {
  return friendlyMessage(error, fallback)
}

function findChapter(chapters: Chapter[], chapterNo: number | undefined): Chapter | null {
  if (chapterNo == null) return null
  return chapters.find((chapter) => chapter.chapter_number === chapterNo) ?? null
}

function titleSuffix(chapter: Chapter | null): string {
  return chapter?.title ? ` · ${chapter.title}` : ''
}

function firstParagraph(content: string): string {
  const paragraph = content
    .split(/\n\s*\n|\n/)
    .map((part) => part.trim())
    .find(Boolean)
  const runes = Array.from(paragraph ?? content)
  const text = runes.slice(0, FALLBACK_RUNE_LIMIT).join('')
  return runes.length > FALLBACK_RUNE_LIMIT ? `${text}…` : text
}

function renderHighlighted(window: ConflictContextWindow) {
  const runes = Array.from(window.text)
  const before = runes.slice(0, window.highlightStart).join('')
  const highlight = runes.slice(window.highlightStart, window.highlightEnd).join('')
  const after = runes.slice(window.highlightEnd).join('')

  return (
    <>
      {before}
      <mark className="rounded-sm bg-amber-200 px-0.5 text-amber-950 dark:bg-amber-400/80 dark:text-amber-950">
        {highlight}
      </mark>
      {after}
    </>
  )
}

function PaneSkeleton() {
  return (
    <div className="space-y-3 rounded-lg border p-4">
      <Skeleton className="h-5 w-40" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-11/12" />
      <Skeleton className="h-4 w-10/12" />
      <Skeleton className="h-4 w-8/12" />
    </div>
  )
}

function ContextPaneView({ pane, row }: { pane: ContextPane; row: ConflictRow }) {
  const { t } = useTranslation()
  const content = pane.query.data?.content ?? pane.chapter?.content ?? ''
  const entityName = row.entity_name || row.entity_id

  if (!pane.chapter) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        {t('conflictContext.chapterMissing')}
      </div>
    )
  }

  if (pane.query.isLoading) {
    return <PaneSkeleton />
  }

  if (!content.trim()) {
    return (
      <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">{pane.title}</p>
        <p className="mt-3">{t('conflictContext.noContent')}</p>
      </div>
    )
  }

  const selection = locateEntity(content, row)
  let window = buildContextWindow(content, selection)

  // 精确匹配失败时退到近似定位, 而不是直接放弃。
  // 「找不到精确匹配」恰恰是最需要上下文的场景 —— 用户此刻要判断的正是
  // "是抽取器错了, 还是我写错了"; 原实现在这里回退去渲染正文首段,
  // 而 markdown 章节的首段就是标题行, 弹窗里只剩一个章节名(E2E 实测)。
  const approximate = window ? null : locateEntityApproximately(content, row)
  if (approximate) {
    window = buildContextWindow(content, approximate.selection)
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <h3 className="text-sm font-semibold tracking-normal">{pane.title}</h3>
      {!window ? (
        <Alert variant="warning" className="mt-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <AlertDescription>{t('conflictContext.entityNotLocated', { entity: entityName })}</AlertDescription>
        </Alert>
      ) : null}
      {approximate ? (
        <Alert variant="warning" className="mt-3">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <AlertDescription>
            {t('conflictContext.entityApproximate', { entity: entityName, matched: approximate.matchedText })}
          </AlertDescription>
        </Alert>
      ) : null}
      <p className="mt-3 max-h-[52vh] overflow-y-auto whitespace-pre-wrap text-sm leading-7 text-foreground">
        {window ? renderHighlighted(window) : firstParagraph(content)}
      </p>
    </div>
  )
}

export default function ConflictContextDialog({ open, row, chapters, onClose }: ConflictContextDialogProps) {
  const { t } = useTranslation()
  const firstChapter = findChapter(chapters, row?.first_chapter)
  const lastChapter = findChapter(chapters, row?.last_chapter)
  const isCrossChapter = Boolean(row && row.first_chapter !== row.last_chapter)

  const firstQuery = useQuery({
    queryKey: ['chapter', firstChapter?.id, 'conflict-context'],
    queryFn: () => chapterApi.get(firstChapter!.id),
    enabled: Boolean(open && firstChapter?.id),
  })

  const lastQuery = useQuery({
    queryKey: ['chapter', lastChapter?.id, 'conflict-context'],
    queryFn: () => chapterApi.get(lastChapter!.id),
    enabled: Boolean(open && isCrossChapter && lastChapter?.id),
  })

  useEffect(() => {
    const error = firstQuery.error ?? lastQuery.error
    if (open && error) toast.error(getErrorMessage(error, t('common.serviceError')))
  }, [firstQuery.error, lastQuery.error, open, t])

  const panes = useMemo<ContextPane[]>(() => {
    if (!row) return []
    const firstTitle = t('conflictContext.earliestTrigger', {
      chapter: row.first_chapter,
      title: titleSuffix(firstChapter),
    })
    if (!isCrossChapter) return [{ chapter: firstChapter, query: firstQuery, title: firstTitle }]
    return [
      { chapter: firstChapter, query: firstQuery, title: firstTitle },
      {
        chapter: lastChapter,
        query: lastQuery,
        title: t('conflictContext.latestTrigger', {
          chapter: row.last_chapter,
          title: titleSuffix(lastChapter),
        }),
      },
    ]
  }, [firstChapter, firstQuery, isCrossChapter, lastChapter, lastQuery, row, t])

  const firstContent = firstQuery.data?.content ?? firstChapter?.content ?? ''
  const lastContent = lastQuery.data?.content ?? lastChapter?.content ?? ''
  const firstWindow = row ? buildContextWindow(firstContent, locateEntity(firstContent, row)) : null
  const lastWindow = row && isCrossChapter ? buildContextWindow(lastContent, locateEntity(lastContent, row)) : null
  const showSameContentHint = Boolean(
    isCrossChapter &&
      firstWindow?.text &&
      lastWindow?.text &&
      firstWindow.text === lastWindow.text,
  )

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose()
      }}
    >
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('conflictContext.title')}</DialogTitle>
        </DialogHeader>

        {row ? (
          <div className="space-y-4">
            <Alert variant="warning" className="text-amber-950 dark:text-amber-100">
              <Info className="mt-0.5 size-4 shrink-0" />
              <AlertTitle>{t('conflictContext.detailLabel')}</AlertTitle>
              <AlertDescription className="mt-1 whitespace-pre-wrap">{row.detail}</AlertDescription>
            </Alert>

            {showSameContentHint ? (
              <Alert variant="warning">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" />
                <AlertDescription>{t('conflictContext.sameContentHint')}</AlertDescription>
              </Alert>
            ) : null}

            <div className={isCrossChapter ? 'grid gap-4 lg:grid-cols-2' : 'grid gap-4'}>
              {panes.map((pane) => (
                <ContextPaneView key={`${pane.title}-${pane.chapter?.id ?? 'missing'}`} pane={pane} row={row} />
              ))}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
